import {
  Dropdown,
  DropdownItem,
  DropdownSeparator,
  DropdownToggle,
  Nav,
  NavGroup,
  NavItem,
  Page,
  PageHeader,
  PageHeaderTools,
  PageSidebar,
  Toolbar,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback } from 'react'
import { connect } from 'react-redux'
import { NavLink, Redirect, Route, Switch } from 'react-router-dom'
import styled from 'styled-components'

import { AboutModal } from '@app/components/about_modal'
import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import { IntegrationsScreen } from '@app/screens/clinic/integrations'
import { NewPatientScreen } from '@app/screens/clinic/new-patient'
import { NewVisitScreen } from '@app/screens/clinic/new-visit'
import { PatientScreen } from '@app/screens/clinic/patient'
import { ClinicPatientList as PatientListScreen } from '@app/screens/clinic/patient-list'
import { ProgramScreen } from '@app/screens/clinic/program'
import { TodaysVisitList } from '@app/screens/clinic/today'
import { VisitScreen } from '@app/screens/clinic/visit'
import VisitListScreen from '@app/screens/clinic/visit-list'
import type { RootState } from '@app/state'

import { InteractionScreen } from './interaction'
import { ClinicProgramList } from './program-list'
import { ProgramSummaryScreen } from './program-summary'

import type { ConnectedProps } from 'react-redux'

const AboutAction = styled.div`
  color: #2b9af3;
  :hover {
    text-decoration: underline;
  }
`

const mapState = (state: RootState) => ({
  account: state.user.account,
})

const connector = connect(mapState)
type PropsFromRedux = ConnectedProps<typeof connector>

interface IAppLayout {
  children?: React.ReactNode
}

const Clinic: React.FunctionComponent<IAppLayout & PropsFromRedux> = ({
  children,
  account,
}) => {
  const [isNavOpen, setIsNavOpen] = React.useState(true)
  const [isMobileView, setIsMobileView] = React.useState(true)
  const [isNavOpenMobile, setIsNavOpenMobile] = React.useState(false)
  const [isUserDropdownOpen, setIsUserDropdownOpen] = React.useState(false)
  const [isAboutDialogOpen, setIsAboutDialogOpen] = React.useState(false)

  const onNavToggleMobile = useCallback(() => {
    setIsNavOpenMobile(!isNavOpenMobile)
  }, [isNavOpenMobile])

  const closeNavMobileMenu = useCallback(() => {
    setIsNavOpenMobile(false)
  }, [])

  const onNavToggle = useCallback(() => {
    setIsNavOpen(!isNavOpen)
  }, [isNavOpen])

  const onPageResize = useCallback(
    (props: { mobileView: boolean; windowSize: number }) => {
      setIsMobileView(props.mobileView)
    },
    [],
  )

  const onUserDropdownToggle = useCallback(() => {
    setIsUserDropdownOpen(!isUserDropdownOpen)
  }, [isUserDropdownOpen])

  const onDropdownSelect = useCallback(() => {
    setIsUserDropdownOpen(false)
  }, [])

  const onAboutClick = useCallback(() => {
    setIsAboutDialogOpen(true)
    setIsUserDropdownOpen(false)
  }, [])

  const onAboutDialogClosed = useCallback(() => {
    setIsAboutDialogOpen(false)
  }, [])

  const userDropdownItems = [
    <DropdownItem key="account">
      <NavLink exact to={'/account/settings'}>
        My Account
      </NavLink>
    </DropdownItem>,
    <DropdownItem key="about" component="button" onClick={onAboutClick}>
      <AboutAction>About</AboutAction>
    </DropdownItem>,
    <DropdownItem key="admin">
      <NavLink exact to={'/admin/settings'}>
        Admin Settings
      </NavLink>
    </DropdownItem>,
    <DropdownSeparator key="separator" />,
    <DropdownItem key="logout">
      <NavLink exact to={'/logout'}>
        Sign Out
      </NavLink>
    </DropdownItem>,
  ]

  const PageToolbar = (
    <PageHeaderTools>
      <Toolbar>
        <ToolbarGroup>
          <ToolbarItem>
            <Dropdown
              isPlain
              onSelect={onDropdownSelect}
              position="right"
              toggle={
                <DropdownToggle onToggle={onUserDropdownToggle}>
                  {account
                    ? `${account.givenName} ${account.familyName}`
                    : `Loading...`}
                </DropdownToggle>
              }
              isOpen={isUserDropdownOpen}
              dropdownItems={userDropdownItems}
            />
          </ToolbarItem>
        </ToolbarGroup>
      </Toolbar>
    </PageHeaderTools>
  )

  const Header = (
    <PageHeader
      logo="intakeDI"
      headerTools={PageToolbar}
      showNavToggle
      isNavOpen={isNavOpen}
      onNavToggle={isMobileView ? onNavToggleMobile : onNavToggle}
    />
  )

  const Navigation = (
    <Nav id="nav-primary-simple" theme="dark">
      <NavGroup title="Clinic">
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/clinic/today'} activeClassName="pf-m-current">
            Today
          </NavLink>
        </NavItem>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/clinic/visits'} activeClassName="pf-m-current">
            Visits
          </NavLink>
        </NavItem>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/clinic/patients'} activeClassName="pf-m-current">
            Patients
          </NavLink>
        </NavItem>
        <RequireFeatureFlag
          key="program_configuration_enabled"
          feature={FeatureFlagId.ProgramConfiguration}
        >
          <NavItem onClick={closeNavMobileMenu}>
            <NavLink
              exact
              to={'/clinic/programs'}
              activeClassName="pf-m-current"
            >
              Programs
            </NavLink>
          </NavItem>
        </RequireFeatureFlag>
      </NavGroup>
      <NavGroup title="Administration">
        <RequireFeatureFlag feature={FeatureFlagId.DataIntegrations}>
          <NavItem onClick={closeNavMobileMenu}>
            <NavLink
              exact
              to={'/clinic/integrations'}
              activeClassName="pf-m-current"
            >
              Integrations
            </NavLink>
          </NavItem>
        </RequireFeatureFlag>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/admin/patients'} activeClassName="pf-m-current">
            Patients
          </NavLink>
        </NavItem>
        <NavItem onClick={closeNavMobileMenu}>
          <NavLink exact to={'/admin'} activeClassName="pf-m-current">
            Settings
          </NavLink>
        </NavItem>
      </NavGroup>
    </Nav>
  )
  const Sidebar = (
    <PageSidebar
      theme="dark"
      nav={Navigation}
      isNavOpen={isMobileView ? isNavOpenMobile : isNavOpen}
    />
  )
  return (
    <Page
      mainContainerId="primary-app-container"
      header={Header}
      sidebar={Sidebar}
      onPageResize={onPageResize}
    >
      <AboutModal isOpen={isAboutDialogOpen} onClose={onAboutDialogClosed} />
      <Switch>
        <Route path="/clinic/today" component={TodaysVisitList} />
        <Route path="/clinic/visits" component={VisitListScreen} />
        <Route path="/clinic/visit/new" component={NewVisitScreen} />
        <Route path="/clinic/visit/:id" component={VisitScreen} />
        <Route path="/clinic/patients" component={PatientListScreen} />
        <Route path="/clinic/programs" component={ClinicProgramList} />
        <Route
          path="/clinic/program-summary/:id"
          component={ProgramSummaryScreen}
        />
        <Route path="/clinic/patient/new" component={NewPatientScreen} />
        <Route path="/clinic/patient/:id" component={PatientScreen} />
        <Route path="/clinic/integrations" component={IntegrationsScreen} />
        <Route path="/clinic/program/:id" component={ProgramScreen} />
        <Route path="/clinic/interaction/:id" component={InteractionScreen} />
        <Route path="*">
          <Redirect to="/404" />
        </Route>
      </Switch>
      {children}
    </Page>
  )
}

export default connector(Clinic)
